import { Box, CloseButton, Icon } from '@chakra-ui/react';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import {
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerCloseButton,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '@ui/components/ModalOrDrawer';
import {
  TransactionError,
  TransactionPending,
} from '@ui/components/TransactionDrawer';
import { useCoinMode } from '@ui/hooks/useCoinMode';
import { useTranslations } from '@ui/i18n';
import { getGardenName } from '@ui/utils/garden';
import { ArrowBack } from '@veno-app/icons';
import type { PropsWithChildren } from 'react';
import React from 'react';

import {
  STEP,
  useGardenNftDrawerStore,
} from '../GardenNftDrawersShared/useGardenNftDrawer';
import { useGrowHelpers } from '../GrowHelpersProvider';

type GardenNftDrawerProps = {
  mode: 'STAKE' | 'UNSTAKE';
};

function GardenNftDrawer({
  mode,
  children,
}: PropsWithChildren<GardenNftDrawerProps>) {
  const t = useTranslations();
  const coinMode = useCoinMode();
  const {
    isOpen,
    onClose,
    mode: activeMode,
    step,
    error,
    setStep,
    numberTransactionSteps,
    currentTransactionStep,
  } = useGardenNftDrawerStore();
  const { gardenType } = useGrowHelpers();

  return (
    <ModalOrDrawer
      variant="wholeScreen"
      placement="bottom"
      scrollBehavior="inside"
      isOpen={isOpen && mode === activeMode}
      onClose={onClose}
      size={{
        base: 'full',
        desktop: 'md',
      }}
    >
      <ModalOrDrawerOverlay bg="bg-gauze" />
      <ModalOrDragDrawerContent>
        <InDesktop>
          <ModalOrDrawerCloseButton />
        </InDesktop>
        <ModalOrDrawerHeader>
          {step === STEP.confirming && (
            <CloseButton
              position="absolute"
              variant={{
                base: 'raised',
                desktop: 'default',
              }}
              onClick={() => setStep(STEP.default)}
            >
              <Icon as={ArrowBack} boxSize="14px" />
            </CloseButton>
          )}
          {mode === 'UNSTAKE' && (
            <>
              {t('Fire helper')}{' '}
              <Box mt="3px" color="text-light" textStyle="caption">
                {getGardenName(coinMode, gardenType)}
              </Box>
            </>
          )}
          {mode === 'STAKE' && (
            <>
              {t('Hire helper')}{' '}
              <Box mt="3px" color="text-light" textStyle="caption">
                {getGardenName(coinMode, gardenType)}
              </Box>
            </>
          )}
        </ModalOrDrawerHeader>
        {children}
        {step === STEP.pending && (
          <TransactionPending.Content
            currentStep={currentTransactionStep}
            nSteps={numberTransactionSteps}
          />
        )}
        {step === STEP.failed && (
          <TransactionError.Content error={error} mx="16px" />
        )}
      </ModalOrDragDrawerContent>
    </ModalOrDrawer>
  );
}

export default GardenNftDrawer;
